import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { Nav, Modal, ModalHeader, ModalBody } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { MdDeleteForever } from "react-icons/md";
import { oauthAppsSelectors, oauthAppsActions } from "../redux/oauth-apps";
import { reduxLoaderSelectors } from "../redux/redux-loader";
import Layout from "../components/layout/layout";
import StyledLoading from "../components/styled/StyledLoading";
import { StyledToolbar } from "../components/styled/ToolbarStyles";
import { ToolbarBox } from "../components/styled/DashboardStyles";
import { StyledTable } from "../components/styled/BlacklistStyles";
import { LabelInput } from "../components/styled/FormStyles";
import StyledButton from "../components/styled/StyledButton";
import { Content, StyledNavItem } from "../components/accountDetails/AccountDetails";

const StyledAppName = styled.div`
  display: flex;
  align-items: center;
  & img {
    max-width: 32px;
    max-height: 32px;
    object-fit: contain;
  }
  & p {
    margin: 0;
    font-weight: 500;
  }
`;

const LogoCell = styled.div`
  background-color: #fff;
  padding: 6px;
  border-radius: 6px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  width: 44px;
  height: 44px;
  & img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const InstallLink = styled.a`
  color: #6c9eff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: #8bb3ff;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #e57373;
  cursor: pointer;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  &:hover {
    color: #ff8a80;
  }
`;

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 450px;
  }
  .modal-header {
    padding: 1rem 2rem;
  }
  button.close span {
    color: red;
    text-shadow: none;
    font-size: 35px;
  }
  .modal-body {
    padding: 2rem 1rem;
  }
`;

const ConfirmInput = styled.input`
  box-shadow: 1px 2px 10px -2px rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  padding: 4px 6px;
  border: none;
  line-height: 28px;
  height: 28px;
  min-width: 200px;
  margin-top: 8px;
`;

const tabs = [
  { id: "list", label: "Apps list" },
  { id: "create", label: "Create app" },
];

// Prod: app.livesession.io → apis.livesession.io/accounts. Labs: app.labs.livesession.io → apis.labs.livesession.io/accounts
function getAccountsAuthorizeBaseUrl() {
  const webappUrl = process.env.REACT_APP_WEBAPP_URL || "https://app.livesession.io";
  try {
    const host = new URL(webappUrl).hostname;
    const isLabs = host.includes("labs.");
    const base = isLabs ? "https://apis.labs.livesession.io/accounts" : "https://apis.livesession.io/accounts";
    return `${base}/v1/oauth2/authorize`;
  } catch {
    return "https://apis.livesession.io/accounts/v1/oauth2/authorize";
  }
}

const CreateAppForm = ({ createOAuthApp, isLoading }) => {
  const [name, setName] = useState("");
  const [redirectURI, setRedirectURI] = useState("");
  const [grantTypes, setGrantTypes] = useState("authorization_code");
  const [scope, setScope] = useState("");
  const [userID, setUserID] = useState("");
  const [trusted, setTrusted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    createOAuthApp({ name, redirectURI, grantTypes, scope, userID, trusted });
  };

  return (
    <form onSubmit={onSubmit}>
      <LabelInput htmlFor="name">
        <span>Name:</span>
        <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
      </LabelInput>

      <LabelInput htmlFor="redirectURI">
        <span>Redirect URI:</span>
        <input
          id="redirectURI"
          type="text"
          value={redirectURI}
          onChange={(e) => setRedirectURI(e.target.value)}
          required
        />
      </LabelInput>

      <LabelInput htmlFor="grantTypes">
        <span>Grant types:</span>
        <input
          id="grantTypes"
          type="text"
          value={grantTypes}
          onChange={(e) => setGrantTypes(e.target.value)}
          required
        />
      </LabelInput>

      <LabelInput htmlFor="scope">
        <span>Scope:</span>
        <input id="scope" type="text" value={scope} onChange={(e) => setScope(e.target.value)} required />
      </LabelInput>

      <LabelInput htmlFor="userID">
        <span>User ID:</span>
        <input id="userID" type="text" value={userID} onChange={(e) => setUserID(e.target.value)} required />
      </LabelInput>

      <LabelInput htmlFor="trusted">
        <span>Trusted:</span>
        <input
          id="trusted"
          type="checkbox"
          checked={trusted}
          onChange={(e) => setTrusted(e.target.checked)}
          style={{ width: "auto" }}
        />
      </LabelInput>

      <StyledButton
        loading={isLoading}
        isLoading={isLoading}
        as="button"
        secondary
        type="submit"
        style={{ margin: "35px auto 0", border: "none" }}
      >
        Create
      </StyledButton>
    </form>
  );
};

const OAuthApps = ({
  getOAuthApps,
  list,
  getOAuthAppsState,
  createOAuthApp,
  createOAuthAppState,
  deleteOAuthApp,
  deleteOAuthAppState,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const activeTab = params.get("tab") || "list";
  const { isDone = false, isLoading = false } = getOAuthAppsState || {};
  const deleteIsDone = deleteOAuthAppState?.isDone ?? false;
  const deleteIsError = deleteOAuthAppState?.isError ?? false;
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, clientID: null, name: null });
  const [deleteConfirmValue, setDeleteConfirmValue] = useState("");

  useEffect(() => {
    if (!isDone || list.length === 0) {
      getOAuthApps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deleteModal.isOpen && deleteIsDone && !deleteIsError) {
      setDeleteModal({ isOpen: false, clientID: null, name: null });
      setDeleteConfirmValue("");
      getOAuthApps();
    }
  }, [deleteModal.isOpen, deleteIsDone, deleteIsError, getOAuthApps]);

  const openDeleteModal = (clientID, name) => {
    setDeleteModal({ isOpen: true, clientID, name });
    setDeleteConfirmValue("");
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, clientID: null, name: null });
    setDeleteConfirmValue("");
  };

  const handleDeleteSubmit = (e) => {
    e.preventDefault();
    if (deleteConfirmValue !== "DELETE" || !deleteModal.clientID) return;
    deleteOAuthApp(deleteModal.clientID);
  };

  const renderHeader = () => (
    <thead>
      <tr>
        <th>App</th>
        <th>Client ID</th>
        <th>Scope</th>
        <th>Install</th>
        <th></th>
      </tr>
    </thead>
  );

  const renderRow = ({ client_id, name, logo_url, redirect_uri, scope }) => {
    const baseAuthorizeUrl = getAccountsAuthorizeBaseUrl();
    const installHref = `${baseAuthorizeUrl}?client_id=${encodeURIComponent(client_id)}&redirect_uri=${encodeURIComponent(
      redirect_uri || ""
    )}&response_type=code`;

    return (
    <tr key={client_id}>
      <td>
        <StyledAppName>
          <LogoCell>
            <img src={logo_url} alt={name} onError={(e) => (e.target.style.display = "none")} />
          </LogoCell>
          <p>{name}</p>
        </StyledAppName>
      </td>
      <td>
        <code style={{ fontSize: 12, background: "rgba(0,0,0,0.2)", padding: "4px 8px", borderRadius: 4 }}>
          {client_id}
        </code>
      </td>
      <td style={{ maxWidth: 280 }}>
        <code style={{ fontSize: 12, background: "rgba(0,0,0,0.2)", padding: "4px 8px", borderRadius: 4 }}>
          {scope || "—"}
        </code>
      </td>
      <td>
        <InstallLink href={installHref} target="_blank" rel="noopener noreferrer">
          Install
        </InstallLink>
      </td>
      <td>
        <DeleteButton type="button" onClick={() => openDeleteModal(client_id, name)} title="Delete app">
          <MdDeleteForever size={18} />
        </DeleteButton>
      </td>
    </tr>
  )};

  return (
    <div>
      <StyledToolbar>
        <ToolbarBox>
          <p>oAuth Apps</p>
          <p>{list.length}</p>
        </ToolbarBox>
      </StyledToolbar>
      <div className="container-fluid" style={{ marginTop: 24 }}>
        <div className="content">
          <Nav tabs>
            {tabs.map((tab) => (
              <StyledNavItem key={tab.id}>
                <Link
                  style={{
                    padding: 20,
                    color: activeTab === tab.id ? "#4663f6" : undefined,
                  }}
                  to={`/oauth-apps?tab=${tab.id}`}
                >
                  {tab.label}
                </Link>
              </StyledNavItem>
            ))}
          </Nav>
          <Content>
            {activeTab === "list" && (
              <>
                {list.length > 0 && (
                  <div style={{ overflowX: "auto", maxWidth: 1600, padding: "15px 0" }} className="mx-auto">
                    <StyledTable style={{ minWidth: 800 }} hover>
                      {renderHeader()}
                      <tbody>{list.map((app) => renderRow(app))}</tbody>
                    </StyledTable>
                  </div>
                )}
                {isDone && list.length === 0 && (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                    <p style={{ margin: 0 }}>No OAuth apps found.</p>
                  </div>
                )}
                {isLoading && <StyledLoading />}
              </>
            )}
            {activeTab === "create" && (
              <CreateAppForm createOAuthApp={createOAuthApp} isLoading={createOAuthAppState?.isLoading} />
            )}
          </Content>
        </div>
      </div>

      <StyledModal
        isOpen={deleteModal.isOpen}
        toggle={closeDeleteModal}
        modalTransition={{ timeout: 20 }}
        backdropTransition={{ timeout: 20 }}
      >
        <ModalHeader toggle={closeDeleteModal}>Delete OAuth app</ModalHeader>
        <ModalBody>
          <p>
            Delete app <strong>{deleteModal.name}</strong> ({deleteModal.clientID})? This cannot be undone.
          </p>
          <form onSubmit={handleDeleteSubmit}>
            <p style={{ marginBottom: 4 }}>Type DELETE to confirm:</p>
            <ConfirmInput
              type="text"
              value={deleteConfirmValue}
              onChange={(e) => setDeleteConfirmValue(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
            />
            <StyledButton
              className="danger"
              isLoading={deleteOAuthAppState?.isLoading}
              disabled={deleteConfirmValue !== "DELETE" || deleteOAuthAppState?.isLoading}
              loading={deleteOAuthAppState?.isLoading}
              type="submit"
              as="button"
              style={{ margin: "16px 0 0", border: "none" }}
            >
              <MdDeleteForever /> Delete
            </StyledButton>
          </form>
        </ModalBody>
      </StyledModal>
    </div>
  );
};

export default connect(
  (state) => ({
    list: oauthAppsSelectors.getOAuthAppsList(state),
    getOAuthAppsState: reduxLoaderSelectors.getLoadingState(state, oauthAppsActions.getOauthApps),
    createOAuthAppState: reduxLoaderSelectors.getLoadingState(state, oauthAppsActions.createOauthApp),
    deleteOAuthAppState: reduxLoaderSelectors.getLoadingState(state, oauthAppsActions.deleteOauthApp),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        getOAuthApps: oauthAppsActions.getOauthApps,
        createOAuthApp: oauthAppsActions.createOauthApp,
        deleteOAuthApp: oauthAppsActions.deleteOauthApp,
      },
      dispatch
    )
)(Layout(OAuthApps));
